import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import useTitle from "../../hooks/useTitle";
import EtBtnFormBasic from "../../components/common/buttons/EtBtnFormBasic";
import PulseLoader from "react-spinners/PulseLoader";
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import { useGetChallengesQuery } from "../challenges/challengesApiSlice";
import { useCreateNewUserChallengeSelfEvalMutation } from "./userChallengeSelfEvalsApiSlice";

const EtAddOrUpdateUserChallengeSelfEvalComp = ({
    mode = 'create', 
    existingEvalData = null, 
    challengeId, 
    userId, 
    userOrgId, 
    trailId, 
    sessionId, 

    // 04 Jul 2026 - Active cohort for this self evaluation.
    cohortId,

    triggeringUrl, 
    onClose
}) => {
  // First Created - 22 Aug 2024
  // Author - Atul Pandey
  // Updated - 23 Aug 2024 - added triggeringUrl to capture and pass the url triggering the modal for it to navigate back to that page upon completion
  // Updated - 23 Aug 2024 - added onClose to close the modal
  // Updated - 31 Aug 2024 - refines to create new as well as update coach user challenge evals
  // Updated - 04 Jul 2026 - cohortId
  // Updated - 02 Aug 2026 - Refined for multiple evaluations

  // 0 - Set Page Title
  useTitle('Create Or Update New Challenge');
  
  // 1a- Set Page Navigation
  const navigate = useNavigate();

  // 1b - Extract incoming data:
//   console.log('Mode:', mode)
//   console.log('ExistingEvalData:', existingEvalData)
//   console.log('ChallengeId:', challengeId)
//   console.log('UserId:', userId)
//   console.log('UserOrgId:', userOrgId)
//   console.log('TrailId:', trailId)
//   console.log('SessionId:', sessionId)
//   console.log('CohortId:', cohortId)        // 04 Jul 2026
//   console.log('TriggeringUrl:', triggeringUrl)

  // 2a - Set the mutation to create the new challenge
  const [createNewUserChallengeSelfEval, {
    isLoading,
    isSuccess,
    isError,
    error,
  }] = useCreateNewUserChallengeSelfEvalMutation();

  // 2b - Get ChallengesData
  const {data: challengesData, isLoading: isChallengeLoading} = useGetChallengesQuery()
  const challenges = challengesData?.ids?.map((id) => {
    const challenge = challengesData?.entities[id]
    return challenge
  })

//   console.log('challenges', challenges)

  // 2c - Filter the challengesData for the given challengdId
  const filteredChallenge = challenges?.find((challenge) => challenge._id === challengeId)
//  console.log('Filtered Challenge', filteredChallenge)

  // 2d - Extract all the challenge dimensions for the filtered challenge
  const challengeDimensions = filteredChallenge?.challengeDimensions
//  console.log('challenge dimensions:', challengeDimensions)


  // 2e - Extract all the challenge dim id data
  const challengeDimIds = challengeDimensions?.map((dimension) => {
    const challengeDimId = dimension?.challengeDimId
    return challengeDimId
  })
// console.log('Challenge Dim Ids', challengeDimIds)
// console.log('Challenge Dim Ids', challengeDimIds?.map(dim => dim.challengeDimensionName))

// 3a - State to keep track of selected challenge dimensions
const [selectedChallengeDims, setSelectedChallengeDims] = useState([]);
// console.log('Selected Challenge Dims', selectedChallengeDims)

// 3b - Create the form data object
  const [formData, setFormData] = useState({
    challengeId: challengeId, 
    userId: userId,
    userOrgId: userOrgId,
    trailId: trailId,
    sessionId: sessionId,  
    
    // 04 Jul 2026 - Cohort required by self evaluation controller.
    cohortId: cohortId,

    // 02 Aug 2026
    evaluationType: 'checkpoint',
    evaluationStatus: 'submitted',
    evaluationReason: '',
    evaluationChangeReason: '',
    
    userChallengeEvals: [
        { 
            userChallengeDimId: "", 
            userChallengeDimSelfScore: "", 
            userChallengeDimSelfEvalText: "",
        },
    ],  
  });

// 02 Aug 2026 - Normalize plain IDs and populated references.
const getIdValue = (value) => {
    if (!value) {
        return ''
    }

    if (typeof value === 'string') {
        return value
    }

    return value?._id || ''
}

// 3c - Use Existing Evals data for existing eval records

// 02 Aug 2026
useEffect(() => {
    if (mode === 'update' && existingEvalData) {
        const normalizedEvaluations =
            existingEvalData.userChallengeEvals?.map(
                (evalItem) => ({
                    ...evalItem,

                    userChallengeDimId:
                        getIdValue(
                            evalItem.userChallengeDimId
                        ),

                    userChallengeDimSelfScore:
                        evalItem.userChallengeDimSelfScore ??
                        '',

                    userChallengeDimSelfEvalText:
                        evalItem.userChallengeDimSelfEvalText ??
                        '',
                })
            ) || []

        setFormData({
            challengeId:
                getIdValue(existingEvalData.challengeId) ||
                challengeId,

            userId:
                getIdValue(existingEvalData.userId) ||
                userId,

            userOrgId:
                getIdValue(existingEvalData.userOrgId) ||
                userOrgId,

            trailId:
                getIdValue(existingEvalData.trailId) ||
                trailId,

            sessionId:
                getIdValue(existingEvalData.sessionId) ||
                sessionId,

            // Send the user's current active cohort for authorization.
            // The backend preserves the historical cohort on the evaluation.
            cohortId:
                cohortId ||
                getIdValue(existingEvalData.cohortId),

            evaluationType:
                existingEvalData.evaluationType ||
                'checkpoint',

            evaluationStatus:
                existingEvalData.evaluationStatus ||
                'submitted',

            evaluationReason:
                existingEvalData.evaluationReason || '',

            evaluationChangeReason: '',

            userChallengeEvals:
                normalizedEvaluations,
        })

        setSelectedChallengeDims(
            normalizedEvaluations.map(
                (evalItem) =>
                    evalItem.userChallengeDimId
            )
        )

        return
    }

    // Create mode must start a fresh evaluation instance.
    setFormData({
        challengeId,
        userId,
        userOrgId,
        trailId,
        sessionId,
        cohortId,

        evaluationType: 'checkpoint',
        evaluationStatus: 'submitted',
        evaluationReason: '',
        evaluationChangeReason: '',

        userChallengeEvals: [
            {
                userChallengeDimId: '',
                userChallengeDimSelfScore: '',
                userChallengeDimSelfEvalText: '',
            },
        ],
    })

    setSelectedChallengeDims([])
}, [
    mode,
    existingEvalData,
    challengeId,
    userId,
    userOrgId,
    trailId,
    sessionId,
    cohortId,
])


// useEffect(() => {
//     if (existingEvalData) {          
        
//         setFormData({
//             challengeId: existingEvalData.challengeId,
//             userId: existingEvalData.userId,
//             userOrgId: existingEvalData.userOrgId,
//             trailId: existingEvalData.trailId,
//             sessionId: existingEvalData.sessionId,

//             // 04 Jul 2026 - Preserve stored cohort for existing records.
//             // Fall back to the active cohort for legacy records without cohortId.
//             cohortId:
//                 existingEvalData?.cohortId?._id ||
//                 existingEvalData?.cohortId ||
//                 cohortId,

//             userChallengeEvals: existingEvalData.userChallengeEvals,
//         });

//         // Update read-only status based on existing evaluations
        
//         const evaluatedDims = existingEvalData.userChallengeEvals.reduce((acc, evalItem) => {
//             acc[evalItem.userChallengeDimId] = true; // Mark evaluated dimensions as read-only
//             return acc;
//         }, {});

//         setSelectedChallengeDims(existingEvalData.userChallengeEvals?.map(evalItem => evalItem.userChallengeDimId) || []);
//     }
// }, [
//     existingEvalData,
//     challengeId, 
//     userId, 
//     userOrgId, 
//     trailId, 
//     sessionId,
//     cohortId,   // 04 Jul 2026
// ]);

// 3d - Filter out already selected challengeDimIds

const availableChallengeDims = challengeDimIds?.filter(dim => !selectedChallengeDims.includes(dim._id));
// console.log('available challenge Dims', availableChallengeDims)

// 3e - - Create options for select box from available challenge dimensions
const challengeDimOptions = availableChallengeDims?.map((dim) => (
        <option key={dim._id} value={dim._id}>
            {dim.challengeDimensionName}
        </option>
  ));

// 4 - Reset form upon successful submission
useEffect(() => {
    if (isSuccess) {
        setFormData({
        challengeId: "", 
        userId: "",
        userOrgId: "",
        trailId: "",
        sessionId: "",  
        
        // 04 Jul 2026
        cohortId:"",

        challengeDimId: "",  
        userChallengeEvals:[{}],
        });
        if (onClose) {
        onClose(); // Close the modal
        } else {
        navigate(triggeringUrl || '/userChallengeSelfEvals'); // Navigate to triggering URL or fallback
        }
    }
}, [
    isSuccess, 
    navigate, 
    onClose, 
    triggeringUrl
]);


// 5a - 04 Jul 2026 - Form field updates / change handling.
//
// Do not mutate evaluation objects directly because existing evaluation data
// may originate from RTK Query and be frozen in development mode.
const handleChange = (e) => {
    const { name, value } = e.target
    const [fieldName, indexValue] = name.split('-')
    const index = Number(indexValue)

    setFormData((prevState) => ({
        ...prevState,
        userChallengeEvals: prevState.userChallengeEvals.map(
            (evaluationItem, evaluationIndex) => {
                if (evaluationIndex !== index) {
                    return evaluationItem
                }

                return {
                    ...evaluationItem,
                    //02 Aug 2026
                     [fieldName]:
                        fieldName ===
                        'userChallengeDimSelfScore'
                            ? value === ''
                                ? ''
                                : Number(value)
                            : value,
                    // [fieldName]: value,
                }
            }
        ),
    }))

    // If a dimension is selected, update the selected dimensions state.
    if (fieldName === 'userChallengeDimId') {
        setSelectedChallengeDims((prevState) => {
            const updatedDims = [...prevState]
            updatedDims[index] = value
            return updatedDims
        })
    }
}

// 5b - 02 Aug 2026 - Handle Metadata changes
const handleMetadataChange = (e) => {
    const { name, value } = e.target

    setFormData((prevState) => ({
        ...prevState,
        [name]: value,
    }))
}

// 5c - Form field updates / change handling
// const handleChange = (e) => {
//     const { name, value } = e.target;
//     const [fieldName, index] = name.split('-');

//     setFormData(prevState => {
//         const updatedEvals = [...prevState.userChallengeEvals];
//         updatedEvals[index][fieldName] = value; // Update the form data for the specific field

//         return { ...prevState, userChallengeEvals: updatedEvals };
//     });

//     // If a dimension is selected, update the selected dimensions state
//     if (fieldName === 'userChallengeDimId') {
//         setSelectedChallengeDims(prevState => {
//             const updatedDims = [...prevState];
//             updatedDims[index] = value; // Store the selected dimension ID at the corresponding index
//             return updatedDims;
//         });
//     }
// };

// 5b - Add new challenge dimension evaluation field
const handleAddEval = () => {
    setFormData(prevState => ({
        ...prevState,
        userChallengeEvals: [
            ...prevState.userChallengeEvals,
            { userChallengeDimId: "", userChallengeDimSelfScore: "", userChallengeDimSelfEvalText: "" }
        ]
    }));
    setSelectedChallengeDims(prevState => [...prevState, ""]); // Add an empty string to track the new dimension selection
};
  
// 5d - Remove challenge dimension evaluation field
const handleRemoveEval = (index) => {
    setFormData(prevState => ({
        ...prevState,
        userChallengeEvals: prevState.userChallengeEvals.filter((_, i) => i !== index)
    }));

    setSelectedChallengeDims(prevState => prevState.filter((_, i) => i !== index)); // Remove the dimension from the selected list
};

// 6 - Form Submit Logic

// 02 Aug 2026
const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.cohortId) {
        alert(
            'Please select a cohort before saving the self evaluation.'
        )
        return
    }

    if (
        !Array.isArray(formData.userChallengeEvals) ||
        formData.userChallengeEvals.length === 0
    ) {
        alert(
            'At least one evaluation dimension is required.'
        )
        return
    }

    // Include only rows where the submitter has started an evaluation.
const evaluationsToSubmit =
    formData.userChallengeEvals.filter(
        (evalItem) =>
            evalItem.userChallengeDimId ||
            evalItem.userChallengeDimSelfScore !== '' ||
            evalItem.userChallengeDimSelfEvalText?.trim()
    )

    if (evaluationsToSubmit.length === 0) {
        alert(
            'Please evaluate at least one challenge dimension.'
        )
        return
    }

    const hasIncompleteStartedEvaluation =
        evaluationsToSubmit.some(
            (evalItem) =>
                !evalItem.userChallengeDimId ||
                evalItem.userChallengeDimSelfScore === '' ||
                evalItem.userChallengeDimSelfScore === undefined ||
                evalItem.userChallengeDimSelfScore === null
        )

    if (hasIncompleteStartedEvaluation) {
        alert(
            'Each selected dimension must include a score.'
        )
        return
    }

    const userChallengeSelfEvalData = {
        challengeId: formData.challengeId,
        userId: formData.userId,
        userOrgId: formData.userOrgId,
        trailId: formData.trailId,
        sessionId: formData.sessionId,
        cohortId: formData.cohortId,

        evaluationType:
            formData.evaluationType,

        evaluationStatus:
            formData.evaluationStatus,

        evaluationReason:
            formData.evaluationReason?.trim() ||
            undefined,

        evaluationChangeReason:
            mode === 'update'
                ? formData
                      .evaluationChangeReason
                      ?.trim() || undefined
                : undefined,

        userChallengeEvals:
            evaluationsToSubmit.map(
                (evalItem) => ({
                    ...evalItem,

                    userChallengeDimSelfScore:
                        Number(
                            evalItem.userChallengeDimSelfScore
                        ),
                })
            ),

        ...(mode === 'update' &&
        existingEvalData?._id
            ? {
                  evaluationId:
                      existingEvalData._id,
              }
            : {}),
    }

    const submitEvaluation = async (
        payload
    ) => {
        try {
            await createNewUserChallengeSelfEval(
                payload
            ).unwrap()
        } catch (submitError) {
            console.error(submitError)
        }
    }

    if (formData.pic) {
        const reader = new FileReader()

        reader.onloadend = async () => {
            await submitEvaluation({
                ...userChallengeSelfEvalData,
                pic: reader.result,
            })
        }

        reader.readAsDataURL(formData.pic)
        return
    }

    await submitEvaluation(
        userChallengeSelfEvalData
    )
}

// Prior Code
// const handleSubmit = async (e) => {
// e.preventDefault();

// // 04 Jul 2026
// if (!formData.cohortId) {
//   alert('Please select a cohort before saving the self evaluation.')
//   return
// }

// // Initialize an object to collect the form data
// const userChallengeSelfEvalData = {
//     challengeId: formData.challengeId,
//     userId: formData.userId,
//     userOrgId: formData.userOrgId,
//     trailId: formData.trailId,
//     sessionId: formData.sessionId,

//     // 04 Jul 2026 - Required by cohort scoped self evaluation controller.
//     cohortId: formData.cohortId,


//     userChallengeEvals: formData.userChallengeEvals,
// };

// if (formData.pic) {
//     const reader = new FileReader();
//     reader.onloadend = async () => {
//     userChallengeSelfEvalData.pic = reader.result;
//     try {
//         await createNewUserChallengeSelfEval(userChallengeSelfEvalData).unwrap();
//     } catch (error) {
//         console.error(error);
//     }
//     };
//     reader.readAsDataURL(formData.pic);
// } else {
//     try {
//     await createNewUserChallengeSelfEval(userChallengeSelfEvalData).unwrap();
//     } catch (error) {
//     console.error(error);
//     }
// }
// };

// 7- Handle Cancel

const handleCancel = () => {
  if (onClose) {
    onClose(); // Close the modal
  } else {
    navigate(triggeringUrl || "/userChallengeSelfEvals"); // Navigate to triggering URL or fallback
  }
};

// 8 - Handle Clear

// 02 Aug 2026
const handleClear = () => {
    if (mode === 'update' && existingEvalData) {
        const normalizedEvaluations =
            existingEvalData.userChallengeEvals?.map(
                (evalItem) => ({
                    ...evalItem,

                    userChallengeDimId:
                        getIdValue(
                            evalItem.userChallengeDimId
                        ),

                    userChallengeDimSelfScore:
                        evalItem.userChallengeDimSelfScore ??
                        '',

                    userChallengeDimSelfEvalText:
                        evalItem.userChallengeDimSelfEvalText ??
                        '',
                })
            ) || []

        setFormData({
            challengeId:
                getIdValue(existingEvalData.challengeId) ||
                challengeId,

            userId:
                getIdValue(existingEvalData.userId) ||
                userId,

            userOrgId:
                getIdValue(existingEvalData.userOrgId) ||
                userOrgId,

            trailId:
                getIdValue(existingEvalData.trailId) ||
                trailId,

            sessionId:
                getIdValue(existingEvalData.sessionId) ||
                sessionId,

            // Current cohort is used for backend validation.
            cohortId:
                cohortId ||
                getIdValue(existingEvalData.cohortId),

            evaluationType:
                existingEvalData.evaluationType ||
                'checkpoint',

            evaluationStatus:
                existingEvalData.evaluationStatus ||
                'submitted',

            evaluationReason:
                existingEvalData.evaluationReason || '',

            evaluationChangeReason: '',

            userChallengeEvals:
                normalizedEvaluations,
        })

        setSelectedChallengeDims(
            normalizedEvaluations.map(
                (evalItem) =>
                    evalItem.userChallengeDimId
            )
        )

        return
    }

    setFormData({
        challengeId,
        userId,
        userOrgId,
        trailId,
        sessionId,
        cohortId,

        evaluationType: 'checkpoint',
        evaluationStatus: 'submitted',
        evaluationReason: '',
        evaluationChangeReason: '',

        userChallengeEvals: [
            {
                userChallengeDimId: '',
                userChallengeDimSelfScore: '',
                userChallengeDimSelfEvalText: '',
            },
        ],
    })

    setSelectedChallengeDims([])
}

// Prior Code
// const handleClear = () => {
// setFormData({
//     challengeId: "", 
//     userId: "",
//     userOrgId: "",
//     trailId: "",
//     sessionId: "",  
    
//     // 04 Jul 2026
//     cohortId:"",
    
//     challengeDimId: "",  
//     userChallengeEvals:[{}],
// });
// };

return (
  <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black scrollbar overflow-auto max-h-[60vh]">
          {isLoading && <PulseLoader color={"#ea580c"} />}
          {isChallengeLoading && <PulseLoader color={"#ea580c"} />}
          {isError && (
              <p className="text-orange-700 font-semi-bold py-2">
                  {error?.data?.message}
              </p>
          )}
          <div className="bg-white p-1 m-1 rounded">

            {/* 02 Aug 2026 */}

            <p className="p-1 m-1 font-bold font-poppins text-xl">
                {mode === 'update'
                    ? `Update Self Evaluation ${
                        existingEvalData
                            ?.evaluationNumber
                            ? `#${existingEvalData.evaluationNumber}`
                            : ''
                    }`
                    : 'Create New Self Evaluation'}
            </p>

              {/* <p className="p-1 m-1 font-bold font-poppins text-xl">Create Self-Evaluation</p> */}

              <div className="bg-gray-200 rounded m-1 p-1">
                  <form onSubmit={handleSubmit} className="m-1 p-1 text-xs font-poppins">
                      {/* 02 Aug 2026 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                            <select
                                name="evaluationType"
                                value={formData.evaluationType}
                                onChange={handleMetadataChange}
                                className="w-full rounded p-2"
                            >
                                <option value="baseline">
                                    Baseline
                                </option>
                                <option value="checkpoint">
                                    Checkpoint
                                </option>
                                <option value="final">
                                    Final
                                </option>
                                <option value="ad_hoc">
                                    Ad Hoc
                                </option>
                            </select>

                            <select
                                name="evaluationStatus"
                                value={formData.evaluationStatus}
                                onChange={handleMetadataChange}
                                className="w-full rounded p-2"
                            >
                                <option value="submitted">
                                    Submitted
                                </option>
                                <option value="draft">
                                    Draft
                                </option>
                            </select>
                        </div>

                        <textarea
                            name="evaluationReason"
                            value={formData.evaluationReason}
                            onChange={handleMetadataChange}
                            placeholder="Reason or context for this evaluation"
                            className="w-full rounded p-2 mb-3"
                        />

                        {mode === 'update' && (
                            <textarea
                                name="evaluationChangeReason"
                                value={
                                    formData.evaluationChangeReason
                                }
                                onChange={handleMetadataChange}
                                placeholder="Reason for correcting this evaluation"
                                className="w-full rounded p-2 mb-3"
                            />
                        )}
                      
                      {/* Challenge Self-Evaluation Scores */}
                      
                  {formData.userChallengeEvals?.map((evalItem, index) => (
                          <div key={index} className="mb-4">
                              {/* Select for Challenge Dimension */}
                              <div className="flex justify-start items-center mb-2">
                                  <select
                                      name={`userChallengeDimId-${index}`}
                                      value={evalItem.userChallengeDimId}
                                      onChange={handleChange}
                                      className="w-full rounded p-2 m-1"
                                  >
                                      <option value="" disabled>Select Challenge Dimension</option>
                                      {/* {challengeDimOptions} */}
                                      {availableChallengeDims?.map(dim => (
                                      <option key={dim._id} value={dim._id}>
                                          {dim.challengeDimensionName}
                                      </option>
                                      ))}
                                      {/* Include the currently selected dimension in the options */}
                                      {evalItem.userChallengeDimId && !availableChallengeDims?.some(dim => dim._id === evalItem.userChallengeDimId) && (
                                          <option 
                                            value={evalItem.userChallengeDimId} 
                                            // selected // 02 Aug 2026
                                          >
                                              {challengeDimIds?.find(dim => dim._id === evalItem.userChallengeDimId)?.challengeDimensionName || "Selected Dimension"}
                                          </option>
                                      )}
                                  </select>
                              </div>
                              {/* Input for Self-Evaluation Score */}
                              <div className="flex justify-start items-center mb-2">
                                  <input
                                      type="number"
                                      name={`userChallengeDimSelfScore-${index}`}
                                      placeholder="Enter Self-Evaluation Score"
                                      value={evalItem.userChallengeDimSelfScore}
                                      onChange={handleChange}
                                      className="w-full rounded p-2 m-1"
                                      
                                      // 02 Aug 2026 - commented out
                                    //   readOnly={mode === 'update' && existingEvalData?.userChallengeEvals?.some(existingEval => existingEval.userChallengeDimId === evalItem.userChallengeDimId && existingEval.userChallengeDimSelfScore !== undefined)}  // 31 Aug 2024
                                  />
                              </div>
                              {/* Textarea for Self-Evaluation Text */}
                              <div className="flex justify-start items-center mb-2">
                                  <textarea
                                      name={`userChallengeDimSelfEvalText-${index}`}
                                      placeholder="Enter Self-Evaluation Text"
                                      value={evalItem.userChallengeDimSelfEvalText}
                                      onChange={handleChange}
                                      className="w-full rounded p-2 m-1"
                                  />
                              </div>
                              {/* Remove Button */}
                              <div className="flex justify-end">
                                  <button
                                      type="button"
                                      onClick={() => handleRemoveEval(index)}
                                      className="flex justify-center items-center bg-red-500 text-white rounded p-1 m-1"
                                  >
                                      <MinusIcon className="h-5 w-5" />
                                  </button>
                              </div>
                          </div>
                      ))}
                      {availableChallengeDims?.length > 0 && (
                          <div className="flex justify-start items-center m-1">
                              <button
                                  type="button"
                                  onClick={handleAddEval}
                                  className="flex justify-center items-center bg-green-500 text-white rounded p-1 m-1"
                              >
                                  <PlusIcon className="h-5 w-5" />
                              </button>
                          </div>
                      )}

                      <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
                          <EtBtnFormBasic
                              name={mode === 'create' ? "Create" : "Update"}
                              type={"create"}
                              handleClick={handleSubmit}
                          />
                          <EtBtnFormBasic
                              name={"Clear"}
                              type={"clear"}
                              handleClick={handleClear}
                          />
                          <EtBtnFormBasic
                              name={"Cancel"}
                              type={"cancel"}
                              handleClick={handleCancel}
                          />
                      </div>
                  </form>
              </div>
          </div>
      </div>

)
}

export default EtAddOrUpdateUserChallengeSelfEvalComp